/**
 * Position-anchored follow-on lifecycle modals for RE Debt.
 *
 * Each modal hits one of the five RealEstateLoanTradingService endpoints:
 *   - PUT /api/credit/re-debt/{id}/paydown
 *   - PUT /api/credit/re-debt/{id}/rate-reset
 *   - PUT /api/credit/re-debt/{id}/amendment
 *   - PUT /api/credit/re-debt/{id}/writedown
 *   - PUT /api/credit/re-debt/{id}/payoff
 *
 * Each endpoint mutates the silo, emits a typed trade event with a stable
 * source_ref (idempotent retries via uq_trade_tx_source_ref), and posts the
 * trade through PositionPostingService so fact_positions +
 * fact_position_loan_attrs stay in lockstep.
 *
 * Per memory feedback_position_anchored_dispose: this entry surface is the
 * authoritative origination point for every post-acquisition lifecycle event.
 * The trade ticket is BUY-only.
 */
import React, { useState, useMemo } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import { ArrowDownCircle, RefreshCw, FileText, AlertTriangle, Banknote, HardHat } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Select, Footer, Button, ErrorBanner
} from './reModalShared';

import { DateField } from '../shared/DateField';

export interface ReDebtPositionLite {
  id: number;
  loanName: string;
  propertyName?: string;
  propertyType?: string;
  status?: string;
  currentPrincipal?: number;
  fundedAmount?: number;
  interestRate?: number;
  spreadBps?: number;
  indexRate?: string;
  rateType?: string;
  maturityDate?: string;
  maxLtvCovenant?: number;
  minDscrCovenant?: number;
  appraisedValue?: number;
}

interface BaseProps {
  position: ReDebtPositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

const today = () => getTodayDate();

const fmtCcy = (n: number | undefined) =>
  n == null ? '$0' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ============ PAYDOWN ============

export const ReDebtPaydownModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [amount, setAmount] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = useMemo(() => {
    const n = Number(amount) || 0;
    return Math.max(0, (position.currentPrincipal ?? position.fundedAmount ?? 0) - n);
  }, [amount, position]);

  const submit = async () => {
    setError(null);
    const n = Number(amount);
    if (!n || n <= 0) { setError('Paydown amount must be > 0'); return; }
    const cap = position.currentPrincipal ?? position.fundedAmount ?? 0;
    if (n > cap) { setError(`Paydown exceeds outstanding ${fmtCcy(cap)}`); return; }

    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/paydown`, {
        amount: n,
        effectiveDate,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to record paydown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Paydown"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<ArrowDownCircle size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(16, 185, 129, 0.06)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#6ee7b7'
        }}>
          Reduces funded balance and emits a REPAY trade through the posting engine.
          Idempotent — re-submitting the same amount + date is a no-op.
        </div>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>Paydown Amount (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 5000000"
            />
          </Field>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Outstanding (current)</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>
              {fmtCcy(position.currentPrincipal ?? position.fundedAmount)}
            </div>
          </Field>
          <Field>
            <Label>Outstanding (after paydown)</Label>
            <div style={{ color: '#34d399', fontWeight: 700 }}>
              {fmtCcy(remaining)}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Confirm Paydown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ RATE RESET ============

export const ReDebtRateResetModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [newAllInRate, setNewAllInRate] = useState<string>('');
  const [newSpreadBps, setNewSpreadBps] = useState<string>(
    position.spreadBps != null ? String(position.spreadBps) : ''
  );
  const [newIndexRate, setNewIndexRate] = useState<string>(position.indexRate || 'SOFR');
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!newAllInRate) { setError('New all-in rate is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/rate-reset`, {
        newAllInRate: Number(newAllInRate),
        newSpreadBps: newSpreadBps ? Number(newSpreadBps) : null,
        newIndexRate: newIndexRate || null,
        effectiveDate,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to apply rate reset');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Floating-Rate Reset"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<RefreshCw size={18} className="text-blue-400" />}
      onClose={onClose}
    >
      <Body>
        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>New All-In Rate (%)</Label>
            <Input
              type="number"
              step="0.0001"
              value={newAllInRate}
              onChange={e => setNewAllInRate(e.target.value)}
              placeholder="e.g. 7.85"
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>New Index</Label>
            <Select value={newIndexRate} onChange={e => setNewIndexRate(e.target.value)}>
              <option value="SOFR">SOFR</option>
              <option value="TERM_SOFR_1M">Term SOFR (1M)</option>
              <option value="TERM_SOFR_3M">Term SOFR (3M)</option>
              <option value="DAILY_SOFR">Daily SOFR (in arrears)</option>
              <option value="LIBOR">LIBOR (legacy)</option>
              <option value="PRIME">Prime</option>
            </Select>
          </Field>
          <Field>
            <Label>New Spread (bps)</Label>
            <Input
              type="number"
              step="1"
              value={newSpreadBps}
              onChange={e => setNewSpreadBps(e.target.value)}
              placeholder="e.g. 275"
            />
          </Field>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Current All-In</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>
              {position.interestRate != null ? `${position.interestRate.toFixed(2)}%` : 'N/A'}
            </div>
          </Field>
          <Field>
            <Label>Current Index + Spread</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>
              {position.indexRate || '—'} + {position.spreadBps ?? '—'} bps
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Apply Reset'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ AMENDMENT ============

export const ReDebtAmendmentModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [newMaturityDate, setNewMaturityDate] = useState<string>(position.maturityDate || '');
  const [newMaxLtvCovenant, setNewMaxLtvCovenant] = useState<string>(
    position.maxLtvCovenant != null ? String(position.maxLtvCovenant) : ''
  );
  const [newMinDscrCovenant, setNewMinDscrCovenant] = useState<string>(
    position.minDscrCovenant != null ? String(position.minDscrCovenant) : ''
  );
  const [newExtensionOptions, setNewExtensionOptions] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/amendment`, {
        newMaturityDate: newMaturityDate || null,
        newMaxLtvCovenant: newMaxLtvCovenant ? Number(newMaxLtvCovenant) : null,
        newMinDscrCovenant: newMinDscrCovenant ? Number(newMinDscrCovenant) : null,
        newExtensionOptions: newExtensionOptions || null,
        effectiveDate,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to apply amendment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Loan Amendment"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<FileText size={18} className="text-purple-400" />}
      onClose={onClose}
    >
      <Body>
        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>New Maturity Date</Label>
            <DateField value={newMaturityDate ?? ''} onChange={iso => setNewMaturityDate(iso)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>New Max LTV Covenant (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={newMaxLtvCovenant}
              onChange={e => setNewMaxLtvCovenant(e.target.value)}
              placeholder="e.g. 75"
            />
          </Field>
          <Field>
            <Label>New Min DSCR Covenant (x)</Label>
            <Input
              type="number"
              step="0.01"
              value={newMinDscrCovenant}
              onChange={e => setNewMinDscrCovenant(e.target.value)}
              placeholder="e.g. 1.20"
            />
          </Field>
        </FieldRow>

        <Field>
          <Label>New Extension Options</Label>
          <Input
            type="text"
            value={newExtensionOptions}
            onChange={e => setNewExtensionOptions(e.target.value)}
            placeholder="e.g. One additional 12-month extension @ 25 bps fee, DSCR ≥ 1.15x"
          />
        </Field>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Apply Amendment'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ WRITEDOWN ============

export const ReDebtWritedownModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [newCarryValue, setNewCarryValue] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('WATCHLIST');
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const writedownAmt = useMemo(() => {
    const cur = position.currentPrincipal ?? position.fundedAmount ?? 0;
    const newCV = Number(newCarryValue) || 0;
    return Math.max(0, cur - newCV);
  }, [newCarryValue, position]);

  const submit = async () => {
    setError(null);
    if (!newCarryValue) { setError('New carry value is required'); return; }
    const newCV = Number(newCarryValue);
    if (newCV < 0) { setError('Carry value cannot be negative'); return; }
    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/writedown`, {
        newCarryValue: newCV,
        newStatus,
        effectiveDate,
        notes: notes || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to record writedown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Impairment / Writedown"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<AlertTriangle size={18} className="text-amber-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(245, 158, 11, 0.06)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#fcd34d'
        }}>
          Marks the loan to a lower carry value and updates status. Emits a MTM/IMPAIRMENT
          event through the posting engine. Recovery requires a separate event.
        </div>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>New Carry Value (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={newCarryValue}
              onChange={e => setNewCarryValue(e.target.value)}
              placeholder="e.g. 42000000"
            />
          </Field>
        </FieldRow>

        <Field>
          <Label>New Status</Label>
          <Select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
            <option value="WATCHLIST">Watchlist</option>
            <option value="DELINQUENT">Delinquent</option>
            <option value="SPECIAL_SERVICING">Special Servicing</option>
            <option value="DEFAULTED">Defaulted</option>
            <option value="REO">REO</option>
          </Select>
        </Field>

        <Field>
          <Label>Notes / Justification</Label>
          <Input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Q1 2026 appraisal showed 22% NOI decline, sponsor walked"
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Current Principal</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>
              {fmtCcy(position.currentPrincipal ?? position.fundedAmount)}
            </div>
          </Field>
          <Field>
            <Label>Writedown Amount</Label>
            <div style={{ color: writedownAmt > 0 ? '#fbbf24' : '#94a3b8', fontWeight: 700 }}>
              {fmtCcy(writedownAmt)}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $danger onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Record Writedown'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ PAYOFF ============

export const ReDebtPayoffModal: React.FC<BaseProps> = ({ position, onClose, onSuccess }) => {
  const [effectiveDate, setEffectiveDate] = useState<string>(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/payoff`, {
        effectiveDate,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to record payoff');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Full Payoff"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<Banknote size={18} className="text-emerald-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(16, 185, 129, 0.06)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#6ee7b7'
        }}>
          Repays full outstanding principal, marks loan PAID_OFF, and emits a REPAY trade
          for the entire balance. Position closes at zero.
        </div>

        <FieldRow>
          <Field>
            <Label>Effective Date</Label>
            <DateField value={effectiveDate ?? ''} onChange={iso => setEffectiveDate(iso)} />
          </Field>
          <Field>
            <Label>Payoff Amount</Label>
            <div style={{ color: '#34d399', fontSize: '1rem', fontWeight: 700, paddingTop: 6 }}>
              {fmtCcy(position.currentPrincipal ?? position.fundedAmount)}
            </div>
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Confirm Payoff'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

// ============ CONSTRUCTION DRAW ============

interface ReDebtConstructionDrawProps extends BaseProps {
  unfundedCommitment?: number;
}

const BUDGET_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'HARD_COSTS', label: 'Hard Costs (vertical construction)' },
  { value: 'SOFT_COSTS', label: 'Soft Costs (architect, legal, permits)' },
  { value: 'LAND', label: 'Land Acquisition' },
  { value: 'INTEREST_RESERVE', label: 'Interest Reserve' },
  { value: 'CONTINGENCY', label: 'Contingency' },
  { value: 'TENANT_IMPROVEMENTS', label: 'Tenant Improvements' },
  { value: 'LEASING_COMMISSIONS', label: 'Leasing Commissions' },
];

export const ReDebtConstructionDrawModal: React.FC<ReDebtConstructionDrawProps> = ({
  position, unfundedCommitment, onClose, onSuccess
}) => {
  const [drawDate, setDrawDate] = useState<string>(today());
  const [amount, setAmount] = useState<string>('');
  const [budgetCategory, setBudgetCategory] = useState<string>('HARD_COSTS');
  const [inspector, setInspector] = useState<string>('');
  const [completionPct, setCompletionPct] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fundedAfter = useMemo(() => {
    const cur = position.fundedAmount ?? 0;
    const n = Number(amount) || 0;
    return cur + n;
  }, [amount, position]);

  const unfundedAfter = useMemo(() => {
    const u = unfundedCommitment ?? 0;
    const n = Number(amount) || 0;
    return Math.max(0, u - n);
  }, [amount, unfundedCommitment]);

  const submit = async () => {
    setError(null);
    const n = Number(amount);
    if (!n || n <= 0) { setError('Draw amount must be > 0'); return; }
    if (unfundedCommitment != null && n > unfundedCommitment) {
      setError(`Draw exceeds unfunded commitment ${fmtCcy(unfundedCommitment)}`);
      return;
    }
    if (!budgetCategory) { setError('Budget category is required'); return; }
    const cp = completionPct ? Number(completionPct) : null;
    if (cp != null && (cp < 0 || cp > 100)) {
      setError('Completion % must be between 0 and 100');
      return;
    }

    // Stable client-side idempotency key — re-submitting the same form is a no-op.
    const idempotencyKey = `re-loan:${position.id}:draw:${drawDate}:${budgetCategory}:${n}`;

    setSubmitting(true);
    try {
      await axios.put(`/api/credit/re-debt/${position.id}/draw`, {
        amount: n,
        budgetCategory,
        drawDate,
        inspector: inspector || null,
        completionPct: cp,
        idempotencyKey,
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to fund draw');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Fund Construction Draw"
      subtitle={`${position.loanName}${position.propertyName ? ` • ${position.propertyName}` : ''}`}
      icon={<HardHat size={18} className="text-orange-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(249, 115, 22, 0.06)',
          border: '1px solid rgba(249, 115, 22, 0.25)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#fdba74'
        }}>
          Funds against the construction budget and emits a DRAWDOWN trade through the
          posting engine. Idempotent — re-submitting the same date + category + amount
          is a no-op.
        </div>

        <FieldRow>
          <Field>
            <Label>Draw Date</Label>
            <DateField value={drawDate ?? ''} onChange={iso => setDrawDate(iso)} />
          </Field>
          <Field>
            <Label>Draw Amount (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 2500000"
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Budget Category</Label>
            <Select value={budgetCategory} onChange={e => setBudgetCategory(e.target.value)}>
              {BUDGET_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Completion (%)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              max="100"
              value={completionPct}
              onChange={e => setCompletionPct(e.target.value)}
              placeholder="e.g. 35"
            />
          </Field>
        </FieldRow>

        <Field>
          <Label>Inspector / Construction Consultant</Label>
          <Input
            type="text"
            value={inspector}
            onChange={e => setInspector(e.target.value)}
            placeholder="e.g. CBRE Project Management — Mark Reeves, PE"
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Funded (current)</Label>
            <div style={{ color: '#94a3b8', fontWeight: 600 }}>
              {fmtCcy(position.fundedAmount)}
            </div>
          </Field>
          <Field>
            <Label>Funded (after draw)</Label>
            <div style={{ color: '#34d399', fontWeight: 700 }}>
              {fmtCcy(fundedAfter)}
            </div>
          </Field>
          <Field>
            <Label>Unfunded (after draw)</Label>
            <div style={{ color: '#fdba74', fontWeight: 700 }}>
              {fmtCcy(unfundedAfter)}
            </div>
          </Field>
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>
      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Funding…' : 'Fund Draw'}
        </Button>
      </Footer>
    </ModalShell>
  );
};
